import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import UnifiedCard from '../../common/Card/UnifiedCard';
import CardInfoOverlay from '../../common/Card/CardInfoOverlay';
import BinderRail from './BinderRail';
import { entryPrice, buildPricesByFinish, formatMoney } from './binderMath';
import './Binders.css';

/**
 * BINDER BROWSER - one binder's cards with the site's standard
 * browsing: search box, left rail of always-visible pill zones (with
 * counts), chips + sort/page-size/view above the grid, pagination and
 * the card info overlay. Used by the Binders page (your own binder,
 * editable) and by profiles (someone's binder, read-only, with the
 * cards that fit YOUR binder highlighted).
 *
 * Optional catalog mode (Binders only): when nothing in the binder
 * matches the typed name, "Search the catalog" flips the grid to live
 * catalog results with the same rail/sort/pagination; clearing the
 * name reverts to the binder and calls onCatalogExit (so fresh adds
 * appear).
 *
 * Props:
 *   items          [{card, binder}] - the binder's entries
 *   entryType      'have' | 'want'
 *   editable       your own binder: quantity stepper + remove
 *   onUpdateEntry, onDeleteEntry   (editable only)
 *   isHighlighted  (card) => bool   read-only views: "fits your binder"
 *   placeholder    search box text
 *   noun           "your collection", "their wishlist", ...
 *   catalog        enable catalog mode
 *   onCatalogExit  called when catalog mode ends
 *   resetKey       filters reset when this changes (tab switch)
 *   barExtra       node shown at the start of the results bar
 */

const BINDER_SORT_OPTIONS = [
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'rarity-asc', label: 'Rarity (Common → Mythic)' },
    { value: 'rarity-desc', label: 'Rarity (Mythic → Common)' },
    { value: 'quantity-desc', label: 'Quantity (High → Low)' },
    { value: 'quantity-asc', label: 'Quantity (Low → High)' },
    { value: 'release-asc', label: 'Release Date (Oldest)' },
    { value: 'release-desc', label: 'Release Date (Newest)' },
    { value: 'price-asc', label: 'Price (Low → High)' },
    { value: 'price-desc', label: 'Price (High → Low)' }
];

const RARITY_RANK = { common: 0, uncommon: 1, rare: 2, mythic: 3, special: 4 };
const EMPTY_FILTERS = { name: '', sets: [], rarities: [], finishes: [], priceMin: '', priceMax: '' };

function BinderBrowser({
    items: rawItems, entryType, editable = false, onUpdateEntry, onDeleteEntry,
    isHighlighted, placeholder, noun, catalog = false, onCatalogExit, resetKey, barExtra,
}) {
    // Sets catalog: names for pills/cards/chips + release dates for sorting
    const [allSets, setAllSets] = useState([]);
    useEffect(() => {
        fetch('/api/sets')
            .then(r => (r.ok ? r.json() : []))
            .then(d => setAllSets(Array.isArray(d) ? d : []))
            .catch(() => { });
    }, []);
    const setInfo = useMemo(() => {
        const map = {};
        allSets.forEach(s => { map[s.setCode] = s; });
        return map;
    }, [allSets]);
    const setNames = useMemo(() => {
        const map = {};
        allSets.forEach(s => { map[s.setCode] = s.setName || s.setCode; });
        return map;
    }, [allSets]);
    const setNameOf = (code) => setNames[code] || code;

    // Entries with their finish-accurate price attached once (price
    // arrays may need JSON parsing - never do that inside a sort)
    const cards = useMemo(() => (rawItems || [])
        .filter(({ card }) => card)
        .map(item => ({ ...item, _price: entryPrice(item.card, item.binder?.finish) })),
    [rawItems]);

    // ---------- filters ----------
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    useEffect(() => { setFilters(EMPTY_FILTERS); setPage(1); }, [resetKey]);

    const patchFilters = useCallback((patch) => {
        setFilters(f => ({ ...f, ...patch }));
        setPage(1);
    }, []);
    const toggleListFilter = useCallback((key, value) => {
        setFilters(f => ({
            ...f,
            [key]: f[key].includes(value) ? f[key].filter(x => x !== value) : [...f[key], value]
        }));
        setPage(1);
    }, []);
    const resetFilters = useCallback(() => { setFilters(EMPTY_FILTERS); setPage(1); }, []);

    const activeCount = (filters.name.trim() ? 1 : 0)
        + filters.sets.length + filters.rarities.length + filters.finishes.length
        + (filters.priceMin !== '' ? 1 : 0) + (filters.priceMax !== '' ? 1 : 0);

    // ---------- display options (same localStorage contract as before) ----------
    const [displayOptions, setDisplayOptions] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('searchFilterBarDisplayOptions') || '{}');
            return {
                cardsPerPage: saved.cardsPerPage || 12,
                displayMode: saved.displayMode || 'standard',
                sortBy: 'name-asc',
            };
        } catch {
            return { cardsPerPage: 12, displayMode: 'standard', sortBy: 'name-asc' };
        }
    });
    const updateDisplay = (patch) => {
        setDisplayOptions(o => {
            const next = { ...o, ...patch };
            try {
                localStorage.setItem('searchFilterBarDisplayOptions', JSON.stringify({
                    cardsPerPage: next.cardsPerPage,
                    displayMode: next.displayMode,
                }));
            } catch { }
            return next;
        });
    };

    // ---------- catalog mode: the box searches the whole catalog ----------
    const [catalogMode, setCatalogMode] = useState(false);
    const [catalogCards, setCatalogCards] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);

    const enterCatalog = () => {
        // fresh scope: only the name rides along - rail filters rebuild
        // against the result set
        setFilters(f => ({ ...EMPTY_FILTERS, name: f.name }));
        setPage(1);
        setCatalogMode(true);
    };

    useEffect(() => {
        if (!catalogMode) return;
        const q = filters.name.trim();
        if (!q) { setCatalogMode(false); return; } // name cleared -> standard view
        if (q.length < 2) { setCatalogCards([]); return; }
        let stale = false;
        setCatalogLoading(true);
        const t = setTimeout(async () => {
            try {
                // Resolve matching names first (fast index), then fetch those
                // printings - the same two-step the site's card search uses
                let names = [];
                if (q.length >= 3) {
                    try {
                        const nameRes = await fetch(`/api/card-names/search?q=${encodeURIComponent(q)}`);
                        if (nameRes.ok) names = (await nameRes.json()).slice(0, 50);
                    } catch { }
                }
                const params = new URLSearchParams();
                if (names.length > 0) params.append('names', names.join('||'));
                else params.append('name', q);
                const res = await fetch(`/api/cards/search?${params}`);
                const data = await res.json();
                if (!stale) {
                    setCatalogCards(res.ok ? (data.cards || []) : []);
                    setPage(1);
                }
            } catch {
                if (!stale) setCatalogCards([]);
            } finally {
                if (!stale) setCatalogLoading(false);
            }
        }, 300);
        return () => { stale = true; clearTimeout(t); };
    }, [catalogMode, filters.name]);

    // Leaving catalog mode lets the page refresh so fresh adds appear
    const wasCatalog = useRef(false);
    useEffect(() => {
        if (wasCatalog.current && !catalogMode) {
            setCatalogCards(null);
            setPage(1);
            if (onCatalogExit) onCatalogExit();
        }
        wasCatalog.current = catalogMode;
    }, [catalogMode, onCatalogExit]);

    // A tab switch exits catalog mode (the filter reset clears the name)
    useEffect(() => { setCatalogMode(false); }, [resetKey]);

    // ---------- one pipeline for both scopes ----------
    const items = useMemo(() => {
        if (!catalogMode) return cards;
        return (catalogCards || []).map(card => ({
            card,
            binder: null,
            _price: entryPrice(card, null), // cheapest finish
        }));
    }, [catalogMode, catalogCards, cards]);

    // Rail zones + counts describe whichever list is showing
    const facets = useMemo(() => {
        const sets = {}, rarities = {}, finishes = {};
        items.forEach(({ card, binder }) => {
            const qty = binder?.quantity || 1;
            sets[card.setCode] = (sets[card.setCode] || 0) + qty;
            const r = (card.rarity || '').toLowerCase();
            if (r) rarities[r] = (rarities[r] || 0) + qty;
            if (binder) {
                const f = (binder.finish || 'nonfoil').toLowerCase();
                finishes[f] = (finishes[f] || 0) + qty;
            } else {
                (Array.isArray(card.finishes) ? card.finishes : []).forEach(fin => {
                    const f = String(fin).toLowerCase();
                    finishes[f] = (finishes[f] || 0) + 1;
                });
            }
        });
        return { sets, rarities, finishes };
    }, [items]);

    const filtered = useMemo(() => {
        const name = filters.name.trim().toLowerCase();
        const pmin = filters.priceMin === '' ? null : parseFloat(filters.priceMin);
        const pmax = filters.priceMax === '' ? null : parseFloat(filters.priceMax);
        return items.filter(({ card, binder, _price }) => {
            // In catalog mode the name IS the server query - results
            // already match it, so only binder items re-check it here
            if (name && binder && !card.name?.toLowerCase().includes(name)) return false;
            if (filters.sets.length > 0 && !filters.sets.includes(card.setCode)) return false;
            if (filters.rarities.length > 0 && !filters.rarities.includes((card.rarity || '').toLowerCase())) return false;
            if (filters.finishes.length > 0) {
                if (binder) {
                    if (!filters.finishes.includes((binder.finish || 'nonfoil').toLowerCase())) return false;
                } else {
                    const cardFinishes = (Array.isArray(card.finishes) ? card.finishes : []).map(f => String(f).toLowerCase());
                    if (!filters.finishes.some(f => cardFinishes.includes(f))) return false;
                }
            }
            if (pmin !== null || pmax !== null) {
                if (_price == null) return false;
                if (pmin !== null && _price < pmin) return false;
                if (pmax !== null && _price > pmax) return false;
            }
            return true;
        });
    }, [items, filters]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        const nameOf = (x) => x.card.name || '';
        const byName = (a, b) => nameOf(a).localeCompare(nameOf(b));
        const release = (x) => setInfo[x.card.setCode]?.release_date || '';
        const qty = (x) => x.binder?.quantity || 0;
        const rar = (x) => RARITY_RANK[(x.card.rarity || '').toLowerCase()] ?? 99;
        switch (displayOptions.sortBy) {
            case 'name-asc': arr.sort(byName); break;
            case 'name-desc': arr.sort((a, b) => byName(b, a)); break;
            case 'rarity-asc': arr.sort((a, b) => rar(a) - rar(b) || byName(a, b)); break;
            case 'rarity-desc': arr.sort((a, b) => rar(b) - rar(a) || byName(a, b)); break;
            case 'quantity-desc': arr.sort((a, b) => qty(b) - qty(a) || byName(a, b)); break;
            case 'quantity-asc': arr.sort((a, b) => qty(a) - qty(b) || byName(a, b)); break;
            case 'release-asc': arr.sort((a, b) => release(a).localeCompare(release(b)) || byName(a, b)); break;
            case 'release-desc': arr.sort((a, b) => release(b).localeCompare(release(a)) || byName(a, b)); break;
            case 'price-asc': arr.sort((a, b) => ((a._price ?? Infinity) - (b._price ?? Infinity)) || byName(a, b)); break;
            case 'price-desc': arr.sort((a, b) => ((b._price ?? 0) - (a._price ?? 0)) || byName(a, b)); break;
            default: break;
        }
        return arr;
    }, [filtered, displayOptions.sortBy, setInfo]);

    const filteredValue = useMemo(() => {
        if (catalogMode || activeCount === 0) return null;
        return sorted.reduce((sum, { binder, _price }) =>
            sum + (_price || 0) * (binder?.quantity || 1), 0);
    }, [sorted, activeCount, catalogMode]);

    const perPage = displayOptions.cardsPerPage;
    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
    const safePage = Math.min(page, totalPages);
    const paginated = useMemo(
        () => sorted.slice((safePage - 1) * perPage, safePage * perPage),
        [sorted, safePage, perPage]
    );

    // Quantity sorts say nothing about catalog printings
    const sortOptions = catalogMode
        ? BINDER_SORT_OPTIONS.filter(o => !o.value.startsWith('quantity'))
        : BINDER_SORT_OPTIONS;
    useEffect(() => {
        if (catalogMode && displayOptions.sortBy.startsWith('quantity')) {
            updateDisplay({ sortBy: 'name-asc' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogMode]);

    // ---------- card info overlay (prev/next across the page) ----------
    const [overlayIdx, setOverlayIdx] = useState(null);
    const [overlayImgErr, setOverlayImgErr] = useState(false);
    useEffect(() => {
        setOverlayIdx(null);
        setOverlayImgErr(false);
    }, [displayOptions.sortBy, filters, safePage, resetKey, catalogMode]);
    const overlayItem = !catalogMode && overlayIdx !== null ? paginated[overlayIdx] : null;
    const overlayCard = overlayItem?.card || null;
    const overlayEntry = overlayItem?.binder || null;
    const overlayPrices = useMemo(() => buildPricesByFinish(overlayCard), [overlayCard]);

    // ---------- chips above the grid ----------
    const priceSummary = filters.priceMin !== '' && filters.priceMax !== ''
        ? `$${filters.priceMin}–$${filters.priceMax}`
        : filters.priceMin !== '' ? `$${filters.priceMin}+` : `up to $${filters.priceMax}`;
    const chips = [];
    if (filters.name.trim()) chips.push({
        key: 'name', label: `“${filters.name.trim()}”`, clear: () => patchFilters({ name: '' }),
    });
    filters.sets.forEach(c => chips.push({ key: `set-${c}`, label: setNameOf(c), clear: () => toggleListFilter('sets', c) }));
    filters.rarities.forEach(r => chips.push({ key: `r-${r}`, label: r, cap: true, clear: () => toggleListFilter('rarities', r) }));
    filters.finishes.forEach(f => chips.push({ key: `f-${f}`, label: f, cap: true, clear: () => toggleListFilter('finishes', f) }));
    if (filters.priceMin !== '' || filters.priceMax !== '') chips.push({
        key: 'price', label: priceSummary, clear: () => patchFilters({ priceMin: '', priceMax: '' }),
    });

    // ---------- pagination (same look the site already has) ----------
    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const maxVisible = 5;
        let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return (
            <div className="pagination">
                <button className="pagination-button" onClick={() => setPage(1)} disabled={safePage === 1}>First</button>
                <button className="pagination-button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>Prev</button>
                {pages.map(p => (
                    <button key={p} className={`pagination-button ${safePage === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button className="pagination-button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>Next</button>
                <button className="pagination-button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>Last</button>
                <span className="pagination-info">Page {safePage} of {totalPages}</span>
            </div>
        );
    };

    const gridClass = `cards-grid${displayOptions.displayMode === 'compact' ? ' cards-grid-compact' : ''}${displayOptions.displayMode === 'list' ? ' cards-grid-list' : ''}`;
    const catalogQuery = filters.name.trim();
    const catalogPending = catalogMode && (catalogQuery.length < 2 || (catalogLoading && !catalogCards));

    return (
        <>
            <div className="bn-boxwrap">
                <input
                    className="bn-box"
                    placeholder={placeholder}
                    value={filters.name}
                    onChange={e => patchFilters({ name: e.target.value })}
                    aria-label="Search these cards"
                />
            </div>

            <div className="os-layout">
                <BinderRail
                    facets={facets}
                    setNames={setNames}
                    filters={filters}
                    activeCount={activeCount}
                    onPatch={patchFilters}
                    onToggleList={toggleListFilter}
                    onReset={resetFilters}
                />

                <div className="os-main">
                    <div className="os-results-bar">
                        {barExtra}
                        {chips.length > 0 && (
                            <div className="os-active-chips">
                                {chips.map(c => (
                                    <button
                                        key={c.key}
                                        className={`os-chip${c.cap ? ' cap' : ''}`}
                                        title="Remove"
                                        onClick={c.clear}
                                    >
                                        {c.label} ×
                                    </button>
                                ))}
                                {chips.length >= 2 && (
                                    <button className="os-chip clear" onClick={resetFilters}>Clear all</button>
                                )}
                            </div>
                        )}
                        <div className="bn-selects">
                            <select
                                className="bn-select"
                                value={displayOptions.sortBy}
                                aria-label="Sort cards"
                                onChange={e => updateDisplay({ sortBy: e.target.value })}
                            >
                                {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <select
                                className="bn-select"
                                value={perPage}
                                aria-label="Cards per page"
                                onChange={e => { updateDisplay({ cardsPerPage: parseInt(e.target.value) }); setPage(1); }}
                            >
                                <option value={12}>12 / page</option>
                                <option value={24}>24 / page</option>
                                <option value={48}>48 / page</option>
                                <option value={96}>96 / page</option>
                            </select>
                            <select
                                className="bn-select"
                                value={displayOptions.displayMode}
                                aria-label="View style"
                                onChange={e => updateDisplay({ displayMode: e.target.value })}
                            >
                                <option value="standard">Standard</option>
                                <option value="compact">Compact</option>
                                <option value="list">List</option>
                            </select>
                        </div>
                    </div>

                    {catalogMode ? (
                        <p className="bn-count">
                            Catalog results{catalogQuery.length >= 2 ? ` for “${catalogQuery}”` : ''}
                            {catalogCards && !catalogPending ? ` · showing ${sorted.length} of ${catalogCards.length} printings` : ''}
                            {' '}· clear the search to return to {noun}
                        </p>
                    ) : sorted.length > 0 && (
                        <p className="bn-count">
                            Showing {(safePage - 1) * perPage + 1}–{(safePage - 1) * perPage + paginated.length} of {sorted.length}
                            {filteredValue !== null && ` · est. ${formatMoney(filteredValue)}`}
                        </p>
                    )}

                    {catalogPending ? (
                        <div className="binders-placeholder">
                            {catalogQuery.length < 2 ? 'Keep typing to search the catalog…' : 'Searching the catalog…'}
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="dash-sec-empty bn-empty">
                            {catalogMode ? (
                                <p>
                                    {catalogCards && catalogCards.length > 0
                                        ? 'No printings match these filters.'
                                        : `Nothing in the catalog matches “${catalogQuery}”.`}
                                </p>
                            ) : (
                                <>
                                    <p>Nothing in {noun} matches these filters.</p>
                                    <div className="bn-empty-actions">
                                        <button className="tk-btn ghost" onClick={resetFilters}>Clear filters</button>
                                        {catalog && catalogQuery && (
                                            <button className="tk-btn gold" onClick={enterCatalog}>
                                                Search the catalog for “{catalogQuery}”
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className={gridClass}>
                                {paginated.map((item, idx) => (
                                    item.binder ? (
                                        <UnifiedCard
                                            key={`${item.card.uuid}-${item.card.setCode}-${item.binder.id || idx}`}
                                            card={item.card}
                                            entry={item.binder}
                                            displayMode={displayOptions.displayMode}
                                            setName={setNameOf(item.card.setCode)}
                                            mode={editable ? 'binder' : 'profile'}
                                            entryType={entryType}
                                            isHighlighted={!!(isHighlighted && isHighlighted(item.card))}
                                            onUpdateEntry={editable ? onUpdateEntry : undefined}
                                            onDeleteEntry={editable ? onDeleteEntry : undefined}
                                            onInfoClick={() => { setOverlayIdx(idx); setOverlayImgErr(false); }}
                                        />
                                    ) : (
                                        <UnifiedCard
                                            key={`${item.card.uuid}-${item.card.setCode}`}
                                            card={item.card}
                                            displayMode={displayOptions.displayMode}
                                            setName={setNameOf(item.card.setCode)}
                                            mode="collection"
                                        />
                                    )
                                ))}
                            </div>
                            {renderPagination()}
                        </>
                    )}
                </div>
            </div>

            {overlayCard && (
                <CardInfoOverlay
                    show={overlayIdx !== null}
                    card={overlayCard}
                    entry={overlayEntry}
                    prices={overlayPrices}
                    setDisplayName={setNameOf(overlayCard.setCode)}
                    imageError={overlayImgErr}
                    onClose={() => { setOverlayIdx(null); setOverlayImgErr(false); }}
                    onImageError={() => setOverlayImgErr(true)}
                    quantityBadge={overlayEntry?.quantity > 1 ? `×${overlayEntry.quantity}` : null}
                    onPrevious={() => { if (overlayIdx > 0) { setOverlayIdx(overlayIdx - 1); setOverlayImgErr(false); } }}
                    onNext={() => { if (overlayIdx < paginated.length - 1) { setOverlayIdx(overlayIdx + 1); setOverlayImgErr(false); } }}
                    hasPrevious={overlayIdx > 0}
                    hasNext={overlayIdx !== null && overlayIdx < paginated.length - 1}
                />
            )}
        </>
    );
}

export default BinderBrowser;
